// aiUsageApi — typed fetcher for /v1/ai/usage (read:billing).
//
// Spec: docs/specs/ai-token-quota.md §6.2. Powers the
// /dashboard/ai-usage detail page (four-way breakdown + recent events).

import { apiFetch } from "../../lib/api-client.js";
import { roleLabel } from "@nexus/shared/labels";

export type AiTokenFeature =
  | "ai_chat"
  | "layout_parse_structure"
  | "layout_parse_furniture";

export interface AiUsageBreakdownByFeature {
  feature: AiTokenFeature | string;
  label_zh: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: number;
  event_count: number;
}

export interface AiUsageBreakdownByUser {
  user_id: string | null;
  display_name: string | null;
  role: string | null;
  total_tokens: number;
  cost_usd: number;
  event_count: number;
  breakdown_by_feature: { feature: AiTokenFeature | string; total_tokens: number }[];
}

export interface AiUsageBreakdownByDay {
  date: string;             // 'YYYY-MM-DD'
  total_tokens: number;
  cost_usd: number;
}

export interface AiUsageRecentEvent {
  id: string;
  created_at: string;
  user_id: string | null;
  user_display_name: string | null;
  feature: AiTokenFeature | string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: number | null;
  reference_kind: string | null;
  reference_id: string | null;
}

export interface AiUsageResponse {
  month: string;            // 'YYYY-MM'
  reset_at: string;
  cap_month_tokens: number;
  used_month_tokens: number;
  percent_used: number;
  /** Tokens left in the one-time grants pool (super_admin-issued).
   *  Doesn't reset monthly; depletes as monthly cap overflow consumes
   *  the pool FIFO. -1 means「unused field」 — older builds returned
   *  no grants info; current backend always returns ≥ 0. */
  grants_remaining: number;
  /** Count of currently-active grant rows (un-revoked + un-expired). */
  grants_active_count: number;
  /** ISO 8601 of the earliest expiring active grant; null if none. */
  grants_next_expiry: string | null;
  /** monthly_remaining + grants_remaining; -1 means unlimited. */
  total_available: number;
  breakdown_by_feature: AiUsageBreakdownByFeature[];
  breakdown_by_user: AiUsageBreakdownByUser[];
  breakdown_by_day: AiUsageBreakdownByDay[];
  recent_events: AiUsageRecentEvent[];
  correlation_id: string | null;
}

export class AiUsageApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

export function friendlyAiUsageError(err: unknown): string {
  if (err instanceof AiUsageApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色無法查看 AI 用量明細。";
    if (err.status === 400 && err.code === "tenant_id_required")
      return "尚未選定操作的單位，請先到「平台 / 租戶」選擇。";
    return `AI 用量資料暫時無法存取（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<AiUsageApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
  } catch { /* tolerate */ }
  return new AiUsageApiError(r.status, code, cid);
}

/** Fetch the AI usage breakdown for the given month. `month` is
 *  optional — server defaults to current calendar month UTC. */
export async function getAiUsage(opts: { month?: string | null; tenantId?: string | null } = {}): Promise<AiUsageResponse> {
  const qs = new URLSearchParams();
  if (opts.month) qs.set("month", opts.month);
  if (opts.tenantId) qs.set("tenant_id", opts.tenantId);
  const r = await apiFetch(`/v1/ai/usage${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AiUsageResponse;
}

/** Human-readable label for an AiTokenFeature. zh-TW. */
export function featureLabelZh(feature: string): string {
  switch (feature) {
    case "ai_chat":                return "AI 對話";
    case "layout_parse_structure": return "樓層平面圖：結構解析";
    case "layout_parse_furniture": return "樓層平面圖：家具偵測";
    default: return feature;
  }
}

/** Friendly role labels — delegates to the shared map so the AI Usage
 *  page renders the same strings as the Users page + AI tool_result
 *  enrichment. Returns "—" for null / empty, otherwise the shared map
 *  (which falls back to the raw role string on unknown values). */
export function roleLabelZh(role: string | null): string {
  if (role === null || role === "") return "—";
  return roleLabel(role);
}
